import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { Tag, Tooltip } from "antd";
import {
    UserOutlined,
} from "@ant-design/icons";

import { IAccessLog } from "../../interfaces";
import AccessLogService from "../../services/http/services/AccessLogService";
import useAuth from "../../hooks/useAuth";
import { EmitError } from "../../utils/EmitError";
import { formatDateStringToDateAndTime, formatDateStringToDate } from "../../utils/Conversores";
import TableActionButton from "../../components/TableActionButton";
import { ThemeContext } from "../../contexts/ThemeContext";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";

export interface ITableLogData extends IAccessLog {
    key: number;
}

export interface ILogFilters {
    search: string;
    panelUserId?: number;
    countyId?: number;
    roleId?: number;
    module?: string;
    method?: string;
    success?: boolean;
    startDate?: Date;
    endDate?: Date;
}

export default function useAccessLogs() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    const { activeCity } = useAuth();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<ITableLogData[]>([]);
    const [selectedLog, setSelectedLog] = useState<IAccessLog | null>(null);
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(20);
    const [total, setTotal] = useState<number>(0);

    const [filters, setFilters] = useState<ILogFilters>({
        search: "",
        countyId: activeCity?.id,
    });

    const [dateRange, setDateRange] = useState<[Date, Date] | null>(null);

    const [selectedUserLabel, setSelectedUserLabel] = useState<string>("");
    const [selectedCityLabel, setSelectedCityLabel] = useState<string>("");
    const [selectedRoleLabel, setSelectedRoleLabel] = useState<string>("");

    const handleOpenModal = useCallback((log: IAccessLog) => {
        setSelectedLog(log);
        setIsModalVisible(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalVisible(false);
        setSelectedLog(null);
    }, []);

    const loadLogs = useCallback(
        async (signal?: AbortSignal) => {
            try {
                setIsLoading(true);

                const response = await AccessLogService.list(
                    {
                        page,
                        perPage,
                        ...filters,
                    },
                    signal!
                );

                const mapped: ITableLogData[] = (response.data || []).map(
                    (log: IAccessLog) => ({
                        ...log,
                        key: log.id,
                    })
                );

                setTableData(mapped);
                setTotal(response.meta?.total || mapped.length);
            } catch (err) {
                if (err instanceof AxiosError && err.name === "CanceledError") {
                    return;
                }
                EmitError(err, true, "access_logs");
            } finally {
                setIsLoading(false);
            }
        },
        [page, perPage, filters]
    );

    useEffect(() => {
        const controller = new AbortController();
        loadLogs(controller.signal);

        return () => controller.abort();
    }, [loadLogs]);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            search: "",
            countyId: activeCity?.id,
        });
        setDateRange(null);
        setSelectedUserLabel("");
        setSelectedCityLabel("");
        setSelectedRoleLabel("");
        setPage(1);
    }, [activeCity?.id]);

    const handleRemoveFilter = useCallback(
        (key: string) => {
            setFilters((old) => {
                const next = { ...old };
                if (key === "search") next.search = "";
                if (key === "panelUserId") {
                    delete next.panelUserId;
                    setSelectedUserLabel("");
                }
                if (key === "countyId") {
                    delete next.countyId;
                    setSelectedCityLabel("");
                }
                if (key === "roleId") {
                    delete next.roleId;
                    setSelectedRoleLabel("");
                }
                if (key === "method") delete next.method;
                if (key === "success") delete next.success;
                if (key === "period") {
                    delete next.startDate;
                    delete next.endDate;
                    setDateRange(null);
                }
                return next;
            });
            setPage(1);
        },
        []
    );

    const activeFilterItems = useMemo((): IActiveFilterItem[] => {
        const items: IActiveFilterItem[] = [];

        if (filters.search && filters.search.trim()) {
            items.push({
                key: "search",
                label: "Busca",
                value: `"${filters.search}"`,
                onClear: () => handleRemoveFilter("search"),
            });
        }

        if (filters.panelUserId) {
            items.push({
                key: "panelUserId",
                label: "Usuário",
                value: selectedUserLabel || `ID #${filters.panelUserId}`,
                onClear: () => handleRemoveFilter("panelUserId"),
            });
        }

        if (filters.countyId && filters.countyId !== activeCity?.id) {
            items.push({
                key: "countyId",
                label: "Município",
                value: selectedCityLabel || `ID #${filters.countyId}`,
                onClear: () => handleRemoveFilter("countyId"),
            });
        }

        if (filters.roleId) {
            items.push({
                key: "roleId",
                label: "Perfil",
                value: selectedRoleLabel || `ID #${filters.roleId}`,
                onClear: () => handleRemoveFilter("roleId"),
            });
        }

        if (filters.method) {
            items.push({
                key: "method",
                label: "Método",
                value: filters.method,
                onClear: () => handleRemoveFilter("method"),
            });
        }

        if (filters.success !== undefined) {
            items.push({
                key: "success",
                label: "Status",
                value: filters.success ? "Apenas Sucesso" : "Apenas Falhas / Erros",
                onClear: () => handleRemoveFilter("success"),
            });
        }

        if (filters.startDate && filters.endDate) {
            items.push({
                key: "period",
                label: "Período",
                value: `${formatDateStringToDate(filters.startDate.toISOString())} até ${formatDateStringToDate(filters.endDate.toISOString())}`,
                onClear: () => handleRemoveFilter("period"),
            });
        }

        return items;
    }, [
        filters,
        activeCity?.id,
        selectedUserLabel,
        selectedCityLabel,
        selectedRoleLabel,
        handleRemoveFilter,
    ]);

    const getMethodTagColor = (method: string) => {
        switch (method?.toUpperCase()) {
            case "GET":
                return "blue";
            case "POST":
                return "green";
            case "PUT":
                return "orange";
            case "PATCH":
                return "purple";
            case "DELETE":
                return "red";
            default:
                return "default";
        }
    };

    const getStatusTagColor = (status: number | null | undefined) => {
        if (!status) return "default";
        if (status >= 200 && status < 300) return "success";
        if (status >= 300 && status < 400) return "cyan";
        if (status >= 400 && status < 500) return "warning";
        return "error";
    };

    const columns = useMemo(
        () => [
            {
                title: "Data / Hora",
                dataIndex: "created_at",
                key: "created_at",
                width: 165,
                render: (value: string) => (
                    <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 500, color: isDark ? "#e2e8f0" : "#334155" }}>
                            {value ? formatDateStringToDateAndTime(value) : "--"}
                        </span>
                    </div>
                ),
            },
            {
                title: "Usuário",
                dataIndex: "panel_user_name",
                key: "panel_user_name",
                width: 190,
                render: (userName: string, record: ITableLogData) => (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <UserOutlined style={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }} />
                            <span
                                style={{
                                    fontWeight: 600,
                                    fontSize: 13,
                                    color: isDark ? "#f0f0f0" : "#1e293b",
                                    maxWidth: 150,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                                title={userName || "Sistema / Anônimo"}
                            >
                                {userName || "Sistema / Anônimo"}
                            </span>
                        </div>
                        {record.role_name && (
                            <span style={{ fontSize: 11, color: isDark ? "#8c8c8c" : "#64748b", paddingLeft: 18 }}>
                                {record.role_name}
                            </span>
                        )}
                    </div>
                ),
            },
            {
                title: "Módulo",
                dataIndex: "module",
                key: "module",
                width: 140,
                render: (val: string) =>
                    val ? (
                        <Tag
                            color="geekblue"
                            style={{
                                borderRadius: 4,
                                fontWeight: 500,
                                fontSize: 11.5,
                                margin: 0,
                            }}
                        >
                            {val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                        </Tag>
                    ) : (
                        <span style={{ color: "#94a3b8", fontSize: 12 }}>--</span>
                    ),
            },
            {
                title: "Método",
                dataIndex: "method",
                key: "method",
                width: 95,
                align: "center" as const,
                render: (method: string) => (
                    <Tag
                        color={getMethodTagColor(method)}
                        style={{
                            fontWeight: 700,
                            borderRadius: 4,
                            fontSize: 11,
                            minWidth: 54,
                            textAlign: "center",
                            margin: 0,
                        }}
                    >
                        {method}
                    </Tag>
                ),
            },
            {
                title: "Endpoint / URL",
                dataIndex: "url",
                key: "url",
                render: (url: string) => (
                    <Tooltip title={url} placement="topLeft">
                        <span
                            style={{
                                fontFamily: "SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                                fontSize: 12,
                                color: isDark ? "#93c5fd" : "#0284c7",
                                maxWidth: 260,
                                display: "inline-block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {url || "--"}
                        </span>
                    </Tooltip>
                ),
            },
            {
                title: "Status",
                dataIndex: "status_code",
                key: "status_code",
                width: 90,
                align: "center" as const,
                render: (status: number | null) =>
                    status ? (
                        <Tag
                            color={getStatusTagColor(status)}
                            style={{
                                fontWeight: 700,
                                borderRadius: 4,
                                fontSize: 11.5,
                                margin: 0,
                            }}
                        >
                            {status}
                        </Tag>
                    ) : (
                        <span style={{ color: "#94a3b8" }}>--</span>
                    ),
            },
            {
                title: "Duração",
                dataIndex: "duration_ms",
                key: "duration_ms",
                width: 100,
                align: "center" as const,
                render: (ms: number | null) =>
                    ms !== null && ms !== undefined ? (
                        <span
                            style={{
                                fontSize: 12,
                                fontWeight: 500,
                                color:
                                    ms > 1500
                                        ? "#ef4444"
                                        : ms > 600
                                            ? "#f59e0b"
                                            : isDark
                                                ? "#94a3b8"
                                                : "#64748b",
                            }}
                        >
                            {ms} ms
                        </span>
                    ) : (
                        <span style={{ color: "#94a3b8" }}>--</span>
                    ),
            },
            {
                title: "Sucesso",
                dataIndex: "success",
                key: "success",
                width: 95,
                align: "center" as const,
                render: (value: boolean) => (
                    <Tag
                        color={value ? "green" : "red"}
                        style={{
                            borderRadius: 4,
                            fontWeight: 600,
                            fontSize: 11,
                            margin: 0,
                        }}
                    >
                        {value ? "Sim" : "Falha"}
                    </Tag>
                ),
            },
            {
                title: "Ações",
                key: "actions",
                width: 75,
                align: "center" as const,
                render: (_: any, record: ITableLogData) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <TableActionButton
                            action="view"
                            tooltip="Ver detalhes completos do log"
                            onClick={() => handleOpenModal(record)}
                        />
                    </div>
                ),
            },
        ],
        [isDark, handleOpenModal]
    );

    return {
        columns,
        tableData,
        isLoading,
        page,
        setPage,
        perPage,
        setPerPage,
        total,
        selectedLog,
        isModalVisible,
        handleOpenModal,
        handleCloseModal,
        filters,
        setFilters,
        dateRange,
        setDateRange,
        selectedUserLabel,
        setSelectedUserLabel,
        selectedCityLabel,
        setSelectedCityLabel,
        selectedRoleLabel,
        setSelectedRoleLabel,
        activeFilterItems,
        handleClearAllFilters,
        handleRemoveFilter,
        loadLogs,
        getMethodTagColor,
        getStatusTagColor,
    };
}